import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";

interface HelpCard {
  id: string;
  title: string;
  description: string;
  icon: string;
  route?: string;
  variant: "dark" | "light";
  size: "large" | "small";
}

const supportCards: HelpCard[] = [
  {
    id: "contact-support",
    title: "Contact Support",
    description: "Connect directly with our support specialists for immediate assistance with your account and service inquiries.",
    icon: "ri-customer-service-2-line",
    route: "/contact-support",
    variant: "dark",
    size: "large",
  },
  {
    id: "dispute-resolution",
    title: "Dispute Resolution Form",
    description: "Submit formal dispute resolution requests through our structured process for comprehensive issue resolution.",
    icon: "ri-file-text-line",
    route: "/dispute-resolution",
    variant: "dark",
    size: "large",
  },
];

const subscriptionCards: HelpCard[] = [
  {
    id: "cancel-subscription",
    title: "Cancelling my subscription",
    description: "Learn how to cancel your subscription and understand the cancellation process.",
    icon: "ri-close-circle-line",
    route: "/faq/cancel-subscription",
    variant: "light",
    size: "small",
  },
  {
    id: "auto-renew",
    title: "Does my subscription automatically renew?",
    description: "Understand how subscription renewals work and how to manage them.",
    icon: "ri-refresh-line",
    route: "/faq/auto-renew",
    variant: "light",
    size: "small",
  },
  {
    id: "never-received",
    title: "What if I never received the product or service?",
    description: "Get help when you haven't received your purchased product or service.",
    icon: "ri-question-line",
    route: "/faq/not-received",
    variant: "light",
    size: "small",
  },
];

const billingCards: HelpCard[] = [
  {
    id: "refund",
    title: "Where is my refund?",
    description: "Track your refund request and understand processing timelines.",
    icon: "ri-refund-2-line",
    route: "/faq/refund",
    variant: "light",
    size: "small",
  },
  {
    id: "unrecognized-charge",
    title: "I don't recognize this charge — what should I do?",
    description: "Report and resolve charges you don't recognize on your account.",
    icon: "ri-bank-card-line",
    route: "/faq/unrecognized-charge",
    variant: "light",
    size: "small",
  },
  {
    id: "unaware-charges",
    title: "I was unaware of the charges",
    description: "Get help with charges you weren't aware of or didn't authorize.",
    icon: "ri-alert-line",
    route: "/faq/unaware-charges",
    variant: "light",
    size: "small",
  },
];

function GeometricShapes() {
  return (
    <div className="geometric-shapes">
      <div className="geo-shape triangle" />
      <div className="geo-shape diamond" />
      <div className="geo-shape circle" />
    </div>
  );
}

function HelpCardComponent({ card, onClick }: { card: HelpCard; onClick: () => void }) {
  const isLarge = card.size === "large";

  if (card.variant === "dark") {
    return (
      <div
        className="support-card-dark"
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      >
        <div className="mb-6">
          <div className={`${isLarge ? "w-14 h-14 md:w-16 md:h-16" : "w-10 h-10 md:w-12 md:h-12"} bg-[#B8422E]/10 border border-[#B8422E]/20 flex items-center justify-center mb-6`}>
            <i className={`${card.icon} text-[#B8422E] ${isLarge ? "text-xl md:text-2xl" : "text-lg md:text-xl"}`} />
          </div>
          <h3 className={`${isLarge ? "text-xl md:text-2xl" : "text-lg md:text-xl"} font-bold text-white mb-3`}>
            {card.title}
          </h3>
          <p className={`${isLarge ? "text-base" : "text-sm"} text-[#6C7278] leading-relaxed`}>
            {card.description}
          </p>
        </div>
        <div className="mt-auto flex items-center gap-2 text-[#B8422E] text-sm font-medium">
          <span>Learn more</span>
          <i className="ri-arrow-right-line" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="support-card-light"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
    >
      <div className="mb-4">
        <div className={`${isLarge ? "w-14 h-14 md:w-16 md:h-16" : "w-10 h-10 md:w-12 md:h-12"} bg-[#F7F5F2] border border-[#E2DED9] flex items-center justify-center mb-4`}>
          <i className={`${card.icon} text-[#B8422E] ${isLarge ? "text-xl md:text-2xl" : "text-lg md:text-xl"}`} />
        </div>
        <h3 className={`${isLarge ? "text-xl md:text-2xl" : "text-lg md:text-xl"} font-bold text-[#1A1C1E] mb-2`}>
          {card.title}
        </h3>
        <p className={`${isLarge ? "text-base" : "text-sm"} text-[#6C7278] leading-relaxed`}>
          {card.description}
        </p>
      </div>
      <div className="mt-auto">
        <i className="ri-arrow-right-line text-[#1A1C1E] text-lg" />
      </div>
    </div>
  );
}

function SectionHeader({ label, title, accent, description }: { label: string; title: string; accent?: string; description: string }) {
  return (
    <div className="mb-10 md:mb-14">
      <p className="section-label mb-4">{label}</p>
      <h2 className="section-title-dark" style={{ lineHeight: 1.2 }}>
        {title}
        {accent && <span className="block mt-2 font-light text-[#6C7278]">{accent}</span>}
      </h2>
      <p className="mt-5 text-sm md:text-base text-[#6C7278] leading-relaxed max-w-2xl">
        {description}
      </p>
    </div>
  );
}

const issueCategories = [
  "Billing & Payments",
  "Subscription Management",
  "Technical Issues",
  "Account Access",
  "General Inquiry",
  "Other",
];

export default function Home() {
  const navigate = useNavigate();
  const handleCardClick = (card: HelpCard) => {
    if (card.route) {
      navigate(card.route);
    }
  };

  return (
    <div className="relative min-h-screen">
      <GeometricShapes />

      <div className="relative z-10">
        <Navbar />

        {/* Hero */}
        <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-center bg-[#0d0d0d] pt-20">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className="flex flex-col items-center text-center py-16 md:py-24">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#6C7278] font-medium mb-6">/ Help Center</p>
              <h1 className="text-4xl md:text-5xl lg:text-[64px] font-extrabold text-white leading-[1.04] tracking-[-0.02em] max-w-3xl">
                Support Built for<br /><span className="text-[#B8422E]">Your Success</span>
              </h1>
              <p className="mt-5 text-sm md:text-[15px] text-[#6C7278] font-light leading-relaxed max-w-md">
                Get immediate assistance, manage your subscriptions, resolve billing issues, and submit formal dispute requests.
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                <Link to="/contact-support" className="accent-btn">
                  Contact Support
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Connect with Support */}
        <section className="bg-[#F7F5F2] py-20 md:py-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <SectionHeader
              label="/ Connect with Support"
              title="Get Help Now"
              accent="Resolve Your Issues"
              description="Get immediate assistance from our dedicated support team or submit formal dispute resolution requests."
            />
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {supportCards.map((card) => (
                <HelpCardComponent key={card.id} card={card} onClick={() => handleCardClick(card)} />
              ))}
            </div>
          </div>
        </section>

        {/* Subscription Management */}
        <section className="bg-white py-20 md:py-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <SectionHeader
              label="/ Subscription Management"
              title="Manage Your Plan"
              accent="Stay in Control"
              description="Manage your subscription settings, understand renewal policies, and resolve service delivery issues."
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {subscriptionCards.map((card) => (
                <HelpCardComponent key={card.id} card={card} onClick={() => handleCardClick(card)} />
              ))}
            </div>
          </div>
        </section>

        {/* Billing & Payments */}
        <section className="bg-[#F7F5F2] py-20 md:py-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <SectionHeader
              label="/ Billing & Payments"
              title="Track Your Money"
              accent="Clear & Transparent"
              description="Resolve billing inquiries, track refunds, and get help with unrecognized charges on your account."
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {billingCards.map((card) => (
                <HelpCardComponent key={card.id} card={card} onClick={() => handleCardClick(card)} />
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#0d0d0d] border-t border-white/[0.06]">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto py-16 md:py-20">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 pb-10 md:pb-12 border-b border-white/[0.06]">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-base font-bold tracking-[0.16em] text-white uppercase">Trevionix Help</span>
                  <span className="w-px h-4 bg-white/20"></span>
                  <span className="text-[11px] tracking-[0.12em] text-[#6C7278] uppercase">LTD</span>
                </div>
                <p className="text-sm text-[#6C7278] max-w-[260px] leading-relaxed">
                  12 Constance Street<br />
                  London, E16 2DQ<br />
                  United Kingdom
                </p>
                <a href="mailto:support@trevionics.com" className="mt-3 flex items-center gap-2 text-sm text-[#6C7278] hover:text-white transition-colors">
                  <i className="ri-mail-line text-[#B8422E] text-xs" />
                  support@trevionics.com
                </a>
                <a href="tel:+447520658798" className="mt-1.5 flex items-center gap-2 text-sm text-[#6C7278] hover:text-white transition-colors">
                  <i className="ri-phone-line text-[#B8422E] text-xs" />
                  +44 7520 658798
                </a>
              </div>
            </div>
            <div className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-[#6C7278]">&copy; 2026 Trevionix LTD. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}